import { AxiosInstance } from 'axios';
import {
  Comment,
  ListCommentsRequest,
  ListCommentsResponse
} from '../types/comments';
import { buildOffsetParams } from '../util/pagination';
import {
  CommentSchema,
  ListCommentsRequestSchema,
  ListCommentsResponseSchema
} from '../z/comments';

export class CommentsClient {
  constructor(
    private readonly http: AxiosInstance
    
  ) {}

  /**
   * List all comments with optional filtering and pagination.
   * 
   * This endpoint returns a paginated list of comments with optional filtering
   * by market ID, event ID, or parent comment ID. Useful for building comment
   * systems or displaying discussions related to specific content.
   * 
   * @example
   * ```typescript
   * // Get comments for a specific market
   * const comments = await client.comments.list({ 
   *   marketId: '123', 
   *   limit: 50 
   * });
   * 
   * // Get top-level comments (no parent)
   * const topComments = await client.comments.list({ 
   *   parentId: null,
   *   limit: 20 
   * });
   * ```
   * 
   * @param params - Optional query parameters for filtering and pagination
   * @param params.limit - Number of comments to return (default: 10)
   * @param params.offset - Number of comments to skip (default: 0)
   * @param params.marketId - Filter by market ID
   * @param params.eventId - Filter by event ID
   * @param params.parentId - Filter by parent comment ID (null for top-level)
   * 
   * @returns Promise<ListCommentsResponse> - Paginated list of comments
   * @throws {Error} When the API request fails or returns invalid data
   * 
   * @see {@link https://docs.polymarket.com/api-reference/comments/list-comments} - API Documentation
   */
  async list(params?: ListCommentsRequest): Promise<ListCommentsResponse> {
    const validatedParams = params ? ListCommentsRequestSchema.parse(params) : {};
    const queryParams = buildOffsetParams(validatedParams);
            try {
      const response = await this.http.get('/comments', { params: queryParams });
      const validatedData = ListCommentsResponseSchema.parse(response.data);
      
            return validatedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to list comments: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get a comment by its unique identifier.
   * 
   * This endpoint returns detailed information about a specific comment,
   * including its content, author, timestamps, and engagement metrics.
   * 
   * @example
   * ```typescript
   * const comment = await client.comments.getById('123');
   * console.log('Content:', comment.content);
   * console.log('Author:', comment.author);
   * console.log('Likes:', comment.likes);
   * console.log('Created:', comment.createdAt);
   * ```
   * 
   * @param id - Comment ID
   * @returns Promise<Comment> - Comment data with metadata
   * @throws {Error} When the comment is not found or the API request fails
   * 
   * @see {@link https://docs.polymarket.com/api-reference/comments/get-comments-by-comment-id} - API Documentation
   */
  async getById(id: string): Promise<Comment> {
            try {
      const response = await this.http.get(`/comments/${id}`);
      const validatedData = CommentSchema.parse(response.data);
      
            return validatedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get comment by ID ${id}: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get comments by user address with optional pagination.
   * 
   * This endpoint returns all comments made by a specific user address,
   * useful for building user profiles or activity feeds.
   * 
   * @example
   * ```typescript
   * // Get user's recent comments
   * const userComments = await client.comments.getByUserAddress('0x123...', { 
   *   limit: 20 
   * });
   * 
   * console.log('User has', userComments.total, 'total comments');
   * console.log('Recent comments:', userComments.data.map(c => c.content));
   * ```
   * 
   * @param address - User wallet address
   * @param params - Optional query parameters for pagination
   * @param params.limit - Number of comments to return (default: 10)
   * @param params.offset - Number of comments to skip (default: 0)
   * 
   * @returns Promise<ListCommentsResponse> - Paginated list of user comments
   * @throws {Error} When the user address is invalid or the API request fails
   * 
   * @see {@link https://docs.polymarket.com/api-reference/comments/get-comments-by-user-address} - API Documentation
   */
  async getByUserAddress(address: string, params?: { limit?: number; offset?: number }): Promise<ListCommentsResponse> {
    const validatedParams = params ? ListCommentsRequestSchema.parse(params) : {};
    const queryParams = buildOffsetParams(validatedParams);
            try {
      const response = await this.http.get(`/comments/user/${address}`, { params: queryParams });
      const validatedData = ListCommentsResponseSchema.parse(response.data);
      
            return validatedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get comments for user ${address}: ${error.message}`);
      }
      throw error;
    }
  }
}
